import { inject } from '@angular/core';
import { Router, type CanActivateFn } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { accountLoaded } from '@reducers/account/account-service.selectors';
import { getPermissionState } from '@reducers/auth/permission.helper';
import type { AppState } from '@reducers/interfaces';
import { hasAbsenceTargetPermission } from '@reducers/orm/absence/absence.permission.helper';
import { AbsenceService, getAbsenceState } from '@reducers/orm/absence/absence.service';
import { getEmployeeTeamDepartments } from '@reducers/orm/employee/employee.selectors';
import type { PermissionOption } from '@sb/shared/permission/util';
import { ToastService } from '@sb/ui/toast';
import { combineLatest, defer, filter, map, of, switchMap, take, type Observable } from 'rxjs';

interface AbsenceTargetGuardOptions {
  /**
   * Use the absence already in the store and fetch only when it is missing. For routes whose
   * component reads the absence from the store and is opened from a view that loaded it: the
   * fetch is gated on a View permission that such a route does not require.
   */
  reuseStoredAbsence?: boolean;
}

/**
 * `PermissionGuard` only asks whether the signed-in user holds one of the route's permissions for
 * themselves. This guard resolves whose absence the modal acts on — the owner of the `:id`
 * absence, or the `employee` param when creating one — and, when that is someone else, re-checks
 * the route's `permission` against that employee's departments, where own permissions grant nothing.
 *
 * Also replaces an existence check on `:id` routes: an absence that cannot be fetched redirects to 404.
 */
export const absenceTargetGuard =
  ({ reuseStoredAbsence = false }: AbsenceTargetGuardOptions = {}): CanActivateFn =>
  (route) => {
    const store = inject<Store<AppState>>(Store);
    const router = inject(Router);
    const absenceService = inject(AbsenceService);
    const toast = inject(ToastService);
    const translate = inject(TranslateService);

    const absenceId: string | undefined = route.params['id'];
    const permissions: PermissionOption = route.data['permission'];

    const isAllowedFor = (targetUserId: string | undefined): Observable<boolean> =>
      combineLatest([store.select(getPermissionState), store.select(getEmployeeTeamDepartments)]).pipe(
        take(1),
        map(([permissionState, employeeTeamDepartments]) => {
          if (!targetUserId || targetUserId === permissionState.userId) {
            return true;
          }

          const allowed = hasAbsenceTargetPermission(
            permissions,
            targetUserId,
            employeeTeamDepartments,
            permissionState,
          );

          if (!allowed) {
            toast.openDanger(translate.instant('You do not have permission to open that page'));
          }

          return allowed;
        }),
      );

    const accountLoaded$ = store.select(accountLoaded).pipe(filter(Boolean), take(1));

    if (!absenceId) {
      return accountLoaded$.pipe(switchMap(() => isAllowedFor(route.params['employee'])));
    }

    const storedAbsence$ = store.select(getAbsenceState).pipe(
      take(1),
      map((state) => state.itemsById[absenceId]),
    );
    const fetchedAbsence$ = defer(() => absenceService.fetch(absenceId)).pipe(
      switchMap((response) => (response ? storedAbsence$ : of(undefined))),
    );
    const absence$ = reuseStoredAbsence
      ? storedAbsence$.pipe(switchMap((absence) => (absence ? of(absence) : fetchedAbsence$)))
      : fetchedAbsence$;

    return accountLoaded$.pipe(
      switchMap(() => absence$),
      switchMap((absence) => (absence ? isAllowedFor(absence.user_id) : of(router.parseUrl('/404')))),
    );
  };
